import { api } from "./client";
import type { PagedQuery, PagedResult } from "@/types/api";

// LLM & Reports v3.1 — read-side queries over previously generated reports
// plus mail-merge helpers used by the broker MBA flow. Generation lives on
// reportGenerationApi (see ./reportTemplates.ts); the v2.0 GenerateReportPayload
// shape is gone.

export type OutputFormat = "Word" | "Excel" | "Pdf";

export interface GeneratedReportDto {
  id: string;
  title: string;
  promptTemplateId: string;
  promptTemplateName: string;
  userId: string;
  outputFormat: OutputFormat;
  createdAt: string;
  blobPath?: string | null;

  // LLM & Reports v3.2 §5.2 — three-stage lifecycle. Older v3.1 reports
  // backfill to "BrandingApplied" with the existing artifact path
  // copied onto docxBlobPath. New v3.2 generations move through
  // Drafted → BrandingApplied → Finalized. Fields are optional so the
  // backend can omit them on legacy DTOs without breaking decoders.
  generationStage?: "Drafted" | "BrandingApplied" | "Finalized" | null;
  draftedDate?: string | null;
  brandingAppliedDate?: string | null;
  finalizedDate?: string | null;
  markdownBlobPath?: string | null;
  docxBlobPath?: string | null;
  pdfBlobPath?: string | null;
  exemplarVersionUsed?: string | null;
  exemplarBlobPathUsed?: string | null;
  tokenBudgetEstimate?: number | null;
  tokenUsageActual?: number | null;
}

export interface GeneratedReportDetailDto extends GeneratedReportDto {
  promptTemplateVersion: number;
  // PR-D: bodyText is the assembled prompt sent to Claude — audit-only,
  // surfaced behind the "View prompt source" debug link in the editor.
  // Do NOT bind editor state to it; bind to editedMarkdown instead.
  bodyText: string;
  isFinalized: boolean;
  brokerId?: string | null;
  dealId?: string | null;
  loanId?: string | null;
  vendorId?: string | null;
  formSubmissionId?: string | null;
  sourceDocumentIds: string[];
  // PR-D: editor surface. originalModelOutput is the immutable copy of
  // what Claude returned at generate / regenerate time; editedMarkdown
  // is the user-editable buffer (Save Draft writes here, Finalize &
  // Download renders from here). Both null on legacy pre-PR-D rows
  // that have neither a captured model output nor a v3.2 markdown blob
  // — the editor renders the "Re-generate to enable editing" notice
  // for those.
  originalModelOutput?: string | null;
  editedMarkdown?: string | null;
  lastEditedAt?: string | null;
  // v3.3 §5: verbatim system+user payload Claude received at generate
  // time, including any one-time edit applied on the Prompt review
  // step. The Prompt source modal renders from this field; null on
  // legacy rows generated before v3.3, in which case the modal falls
  // back to bodyText.
  renderedPrompt?: string | null;
}

export interface MailMergeFieldDto {
  token: string;
  label: string;
  sample?: string | null;
}

export interface MailMergePayload {
  promptTemplateId: string;
  title: string;
  brokerId?: string | null;
  dealId?: string | null;
  loanId?: string | null;
  vendorId?: string | null;
  extraFields: Record<string, string | null>;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

export const reportsApi = {
  list: (query?: PagedQuery) =>
    api.get<PagedResult<GeneratedReportDto>>("/reports", query),
  get: (id: string) => api.get<GeneratedReportDetailDto>(`/reports/${id}`),
  updateBody: (id: string, bodyText: string) =>
    api.put<GeneratedReportDetailDto>(`/reports/${id}`, { bodyText }),

  finalize: async (id: string, bodyText: string): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/reports/${id}/finalize`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bodyText }),
    });
    if (!resp.ok) throw new Error(`Finalize failed (${resp.status})`);
    return resp.blob();
  },

  exportDocx: async (id: string): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/reports/${id}/export`, {
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
    return resp.blob();
  },

  // D6: the branded PDF stored beside the branded docx (PdfBlobPath).
  exportPdf: async (id: string): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/reports/${id}/export/pdf`, {
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
    return resp.blob();
  },

  // PR-D: render EditedMarkdown through Pandoc + active brand exemplar
  // and stream the resulting docx. Replaces the v3.1 finalize endpoint
  // (which took bodyText in the request body) — the new endpoint reads
  // EditedMarkdown straight from the row, so the frontend doesn't need
  // to round-trip the buffer.
  finalizeAndDownload: async (id: string): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/reports/${id}/finalize-and-download`, {
      method: "POST",
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`Finalize & download failed (${resp.status})`);
    return resp.blob();
  },

  // PR-D: re-run Claude against the same submission/template and
  // overwrite OriginalModelOutput + EditedMarkdown. Existing edits are
  // lost — frontend confirms with the user before calling this.
  regenerate: (id: string) =>
    api.post<GeneratedReportDetailDto>(`/reports/${id}/regenerate`, undefined),

  mailMergeFields: (promptTemplateId: string, brokerId?: string) =>
    api.get<MailMergeFieldDto[]>("/reports/mail-merge/fields", {
      promptTemplateId,
      brokerId,
    }),

  mailMerge: async (body: MailMergePayload): Promise<Blob> => {
    const resp = await fetch(`${API_BASE}/reports/mail-merge`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw new Error(`Mail-merge failed (${resp.status})`);
    return resp.blob();
  },
};

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
